import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import AlgorithmsPage from "./pages/AlgorithmsPage";

import AlgorithmsDetailsPage from "./pages/AlgorithmsDetailsPage";
import { AuthProvider, useAuth } from "./AuthContext";
import AlgorithmEntryForm from "./pages/AlgorithmEntryForm";
import { AlgorithmProvider, useAlgorithm } from "./pages/AlgorithmContext";
import { MessageProvider } from "./pages/MessageContext";
import MessageDisplay from "./components/MessageDisplay";
import LeftNavPane from "./components/LeftNavPane";
import MobileNav from "./components/MobileNav";
import LandingPage from "./pages/LandingPage";
import UserProfile from "./components/UserProfile";
import FeedbackButton from "./components/FeedbackButton";
import { Menu, Code2 } from "lucide-react";
import { Helmet } from "react-helmet";
import { logPageView } from "./analytics";
import SystemDesignPage from "./pages/SystemDesignPage";
import SystemDesignDetailPage from "./pages/SystemDesignDetailPage";
import SystemDesignFormPage from "./pages/SystemDesignFormPage";
import BehavioralPage from "./pages/BehavioralPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import BlogAdminPage from "./pages/BlogAdminPage";
import BlogEditorPage from "./pages/BlogEditorPage";

const PrivateRoute: React.FC<{ element: React.ReactElement }> = ({
  element,
}) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return user ? element : <Navigate to="/signin" replace />;
};

const AppContent: React.FC = () => {
  const { user, loading } = useAuth();
  const { algorithms } = useAlgorithm();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const [difficultyStats, setDifficultyStats] = useState({
    easy: 0,
    medium: 0,
    hard: 0,
  });

  useEffect(() => {
    const stats = algorithms.reduce(
      (acc, algo) => {
        if (algo.solved) {
          if (algo.level.toLowerCase() === "easy") acc.easy++;
          else if (algo.level.toLowerCase() === "medium") acc.medium++;
          else if (algo.level.toLowerCase() === "hard") acc.hard++;
        }
        return acc;
      },
      { easy: 0, medium: 0, hard: 0 },
    );
    setDifficultyStats(stats);
  }, [algorithms]);

  useEffect(() => {
    logPageView(location.pathname);
  }, [location]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <MessageDisplay />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/*"
          element={
            <div className="flex h-screen">
              {/* Desktop sidebar */}
              <div className="hidden lg:block relative">
                <LeftNavPane
                  difficultyStats={difficultyStats}
                  isCollapsed={isCollapsed}
                  setIsCollapsed={setIsCollapsed}
                />
                {user && <UserProfile isCollapsed={isCollapsed} />}
              </div>

              {/* Mobile sidebar overlay */}
              {isMobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                  <div
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    onClick={() => setIsMobileMenuOpen(false)}
                  />
                  <div className="relative h-full">
                    <LeftNavPane
                      difficultyStats={difficultyStats}
                      isCollapsed={false}
                      setIsCollapsed={() => {}}
                    />
                    {user && <UserProfile isCollapsed={false} />}
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-x-hidden pb-16 lg:pb-0">
                {/* Mobile header */}
                <div className="sticky top-0 z-30 lg:hidden bg-gray-950/95 backdrop-blur-lg border-b border-gray-800">
                  <div className="flex items-center justify-between px-4 h-14">
                    <button
                      onClick={() => setIsMobileMenuOpen(true)}
                      className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                      aria-label="Open menu"
                    >
                      <Menu className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center">
                        <Code2 className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-base font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                        AlgoBuddy
                      </span>
                    </div>
                    <div className="w-10" />
                  </div>
                </div>

                <main>
                  <Routes>
                    <Route
                      path="/behavioral"
                      element={<PrivateRoute element={<BehavioralPage />} />}
                    />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route
                      path="/admin/blog"
                      element={<PrivateRoute element={<BlogAdminPage />} />}
                    />
                    <Route
                      path="/admin/blog/new"
                      element={<PrivateRoute element={<BlogEditorPage />} />}
                    />
                    <Route
                      path="/admin/blog/edit/:id"
                      element={<PrivateRoute element={<BlogEditorPage />} />}
                    />
                    {/* Redirect to algorithms page if no other route matches */}
                    <Route
                      path="*"
                      element={<Navigate to="/algorithms" replace />}
                    />
                    <Route
                      path="/system-design"
                      element={<PrivateRoute element={<SystemDesignPage />} />}
                    />
                    <Route
                      path="/system-design/:id"
                      element={
                        <PrivateRoute element={<SystemDesignDetailPage />} />
                      }
                    />
                    <Route
                      path="/system-design/new"
                      element={<SystemDesignFormPage />}
                    />
                    <Route
                      path="/system-design/edit/:id"
                      element={<SystemDesignFormPage />}
                    />
                    <Route
                      path="/behavioral"
                      element={<PrivateRoute element={<BehavioralPage />} />}
                    />
                  </Routes>
                </main>
              </div>
              <MobileNav />
              <FeedbackButton />
            </div>
          }
        />
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <Helmet>
        <title>AlgoBuddy - Master Your Technical Interviews</title>
        <meta
          name="description"
          content="Organize, track, and review your interview preparation with AlgoBuddy. Master algorithms and ace your technical interviews."
        />
      </Helmet>

      <AuthProvider>
        <MessageProvider>
          <AlgorithmProvider>
            <AppContent />
          </AlgorithmProvider>
        </MessageProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
